// Port of webapp/src/components/cardBadges.tsx — the small overlay chips a
// card shows in Gallery (and the compact preview under a Kanban title).

import {ReactElement} from 'react'
import {Block} from '../types/block'
import {Card} from '../types/card'
import {CommentBlock, useComments} from '../store/comments'
import {Icon} from './common/icons'

export function commentsOfCard(state: {comments: Record<string, CommentBlock>}, cardId: string): CommentBlock[] {
    return Object.values(state.comments).
        filter((c) => c.parentId === cardId && c.deleteAt === 0).
        sort((a, b) => a.createAt - b.createAt)
}

export function countCommentsOfCard(comments: Record<string, CommentBlock>, cardId: string): number {
    return commentsOfCard({comments}, cardId).length
}

export function useCommentCount(cardId: string): number {
    return useComments((state) => Object.values(state.comments).filter((c) => c.parentId === cardId && c.deleteAt === 0).length)
}

export type CardPreview = {
    hasImage: boolean
    text: string
}

/** First text-ish content block, mirroring what the badge shows as a preview. */
export function cardPreview(card: Card, blocksById: Record<string, Block>): CardPreview {
    for (const entry of card.fields.contentOrder) {
        const ids = typeof entry === 'string' ? [entry] : entry
        for (const id of ids) {
            const block = blocksById[id]
            if (!block || block.deleteAt !== 0) {
                continue
            }
            if (block.type === 'image') {
                return {hasImage: true, text: ''}
            }
            if (block.title.trim()) {
                return {hasImage: false, text: block.title.trim()}
            }
        }
    }
    return {hasImage: false, text: ''}
}

export default function CardBadges({card, blocksById, commentCount}: {card: Card, blocksById: Record<string, Block>, commentCount: number}): ReactElement | null {
    const preview = cardPreview(card, blocksById)
    if (!preview.text && !preview.hasImage && !commentCount) {
        return null
    }
    return (
        <div className='flex items-center gap-2 text-xxs opacity-70'>
            {preview.hasImage && <Icon name='image' size={12}/>}
            {preview.text && <span className='truncate'>{preview.text}</span>}
            {commentCount > 0 && (
                <span className='flex shrink-0 items-center gap-1'>
                    <Icon name='message' size={12}/>
                    {commentCount}
                </span>
            )}
        </div>
    )
}
